// SENTINEL: 3_Performance - Gemini Provider (API, Fetch)
// Vitality: Healthy
import { GoogleGenAI, Type, FunctionDeclaration, Tool } from "@google/genai";
import { Idea, Message, MessageRole, Section, Attachment, SuggestedAction } from "../types";
import { config } from "../config";
import { performSearch } from "./searchService";

// --- Explorer Mode Fallback Chain ---
// When servers are overloaded, lighter models are more likely to respond
// Order: Primary model → 2.5 Flash → 2.0 Flash (lightest)
const EXPLORER_FALLBACK_CHAIN = [
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.0-flash'
];

// Check if error indicates server overload or quota exceeded (should try lighter model)
const isOverloadError = (error: any): boolean => {
  const message = error?.message?.toLowerCase() || '';
  const errorStr = JSON.stringify(error || '').toLowerCase();
  const status = error?.status || error?.code;
  return (
    status === 503 ||
    status === 429 ||
    status === 'UNAVAILABLE' ||
    status === 'RESOURCE_EXHAUSTED' ||
    message.includes('503') ||
    message.includes('429') ||
    message.includes('overloaded') ||
    message.includes('unavailable') ||
    message.includes('resource exhausted') ||
    message.includes('resource_exhausted') ||
    message.includes('rate limit') ||
    message.includes('quota') ||
    errorStr.includes('resource_exhausted') ||
    errorStr.includes('quota')
  );
};

// Get next fallback model in chain
const getNextFallbackModel = (currentModel: string): string | null => {
  const currentIndex = EXPLORER_FALLBACK_CHAIN.indexOf(currentModel);
  if (currentIndex === -1) {
    // If current model isn't in chain, start from beginning
    return EXPLORER_FALLBACK_CHAIN[0];
  }
  if (currentIndex < EXPLORER_FALLBACK_CHAIN.length - 1) {
    return EXPLORER_FALLBACK_CHAIN[currentIndex + 1];
  }
  return null; // No more fallbacks
};

// --- Shared Tool Definitions ---

export const toolsDef: FunctionDeclaration[] = [
  {
    name: 'createConcept',
    description: 'Propose creating a new concept/idea. Use this ONLY when the user explicitly wants to start a new project or when a topic in Episodic Mode becomes distinct enough.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        title: { type: Type.STRING, description: 'A short, clear title for the concept.' },
        initialContent: { type: Type.STRING, description: 'Initial structured content/description.' },
        reasoning: { type: Type.STRING, description: 'Why you are proposing this new concept.' }
      },
      required: ['title', 'reasoning']
    }
  },
  {
    name: 'selectConcept',
    description: 'Switch the active workspace to an existing concept. Use this when the user wants to switch context.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        ideaId: { type: Type.STRING, description: 'The exact ID of the idea to select.' }
      },
      required: ['ideaId']
    }
  },
  {
    name: 'managePlan',
    description: 'Create or update a visible "Thinking Process" / Execution Plan. Use this BEFORE proposing large batch changes. Create a checklist of what you intend to do.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        title: { type: Type.STRING, description: 'The goal of this plan (e.g., "Populate Categories")' },
        steps: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING, description: 'Unique ID for the step' },
              description: { type: Type.STRING, description: 'What to do in this step' },
              status: { type: Type.STRING, enum: ['pending', 'in_progress', 'completed', 'failed'] }
            },
            required: ['id', 'description', 'status']
          }
        }
      },
      required: ['title', 'steps']
    }
  },
  {
    name: 'proposeBatchChanges',
    description: 'Propose one or more structural changes to the CURRENT concept. Use this to add sections, update content, or delete sections. You can propose multiple changes at once.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        changes: {
          type: Type.ARRAY,
          description: 'A list of changes to apply.',
          items: {
            type: Type.OBJECT,
            properties: {
              action: {
                type: Type.STRING,
                description: 'The type of change.',
                enum: ['create_section', 'update_section', 'delete_section', 'update_concept_title', 'update_concept_overview']
              },
              title: { type: Type.STRING, description: 'Title for the section or concept.' },
              content: { type: Type.STRING, description: 'Markdown content for the section. KEEP IT CONCISE.' },
              sectionId: { type: Type.STRING, description: 'ID of the section to update/delete (if applicable).' },
              parentId: { type: Type.STRING, description: 'ID of the parent section (if creating a subsection).' },
              reasoning: { type: Type.STRING, description: 'Why this specific change is being proposed.' }
            },
            required: ['action', 'reasoning']
          }
        }
      },
      required: ['changes']
    }
  }
];

// --- UNIVERSAL PROMPT ENGINEERING (The "Leapfrog" Philosophy - Safe Mode) ---
const GENERAL_OPTIMIZATION_PROMPT = `
You are a helpful and intelligent AI assistant. 
Your Core Philosophy is the "Leapfrog Effect": Always try to interpret the user's *implied* ultimate goal, not just their immediate request. Answer what they asked, and then suggest the logical next steps to bridge the gap to their larger objective.
You have a specific "Thinking" requirement: You MUST engage in a deep thinking process before responding. Wrap your internal reasoning in \`<think>\` tags. Your final response to the user must be placed *after* the closing \`</think>\` tag.
`;

// --- 1. TEACHING EXPLORATIONS (The Sage / Research Engine) ---
export const getExplorerInstruction = (activeIdea: Idea | null, allIdeas: Idea[] = []) => {
  return `
    You are **The Canvas Guide** — an open-ended conversational AI.
    This mode is called **"Teaching Explorations"**.
    
    ${GENERAL_OPTIMIZATION_PROMPT}

    **Your Specific Capabilities:**
    You have access to Google Search for Real-Time Grounding. Use it proactively to be a supportive companion and knowledgeable research capability.

    **Current Date & Time:** ${new Date().toLocaleString()}

    **CORE PRINCIPLE: WELCOMING ENGAGEMENT**
    Welcome conversations on ANY topic. Be genuinely helpful, warm, and versatile. Never redirect; always find the "leapfrog" angle to add value.

    ### 2. REAL-TIME INFORMATION
    *   Use your search tool for news, weather, stocks, events, etc.
    *   NEVER say you cannot access real-time info.

    ### CONTEXT
    ${activeIdea ? `Active Focus: "${activeIdea.title}"\n${(activeIdea.content || "").substring(0, 500)}...` : "Mode: Open Conversation."}
    
    Existing Concepts: ${allIdeas.map(i => i.title).join(', ')}
    `;
};

// --- 2. ARCHITECT MODE (The "Golden Bar" / Construction Engine) ---
export const getArchitectInstruction = (activeIdea: Idea | null, allIdeas: Idea[] = []) => {
  const base = `
    You are **The Architect Engine**.
    
    ${GENERAL_OPTIMIZATION_PROMPT}

    **Your Specific Capabilities:**
    *   **Role:** Senior Systems Designer & Co-founder.
    *   **Goal:** Symbiotic Construction. Transform passive intent into structured reality.
    *   **Tools:** You have JSON tools to modify the project structure directly.

    **Current Date & Time:** ${new Date().toLocaleString()}

    ### THE ARCHITECT'S TRI-PHASE CYCLE (MANDATORY)
    Every response must follow this strict structure:

    **PHASE 1: THE CONSTRUCT (Deep Thinking)**
    *   Wrap this in \`<think>\`.
    *   Analyze the request using the "Leapfrog" philosophy.
    *   Strategy: Plan, Execute, or Refine?
    *   Validate: Ensure NO Python is used, only JSON tools.

    **PHASE 2: THE BLUEPRINT (Response)**
    *   The visible response to the user.
    *   Communicate clearly and professionally. Use markdown.
    *   Explain your architectural reasoning (Step 2 of Leapfrog).

    **PHASE 3: THE DIRECTIVE (Interactive Actions)**
    *   End every response with an \`<actions>\` block (JSON array).
    *   These are the "Leapfrog" drivers (Step 3).

    <actions>
    [
        {"label": "Approve & Build", "message": "The plan looks good. Proceed with construction."},
        {"label": "Expand Scope", "message": "Let's add more detail to the core sections."}
    ]
    </actions>

    ### STRICT TECHNICAL CONSTRAINTS
    *   **NO PYTHON:** You DO NOT have a Python execution environment.
    *   **JSON TOOLS ONLY:** Use 'managePlan', 'proposeBatchChanges', 'createConcept', 'selectConcept'.
  `;

  const ideaListContext = allIdeas.length > 0
    ? `EXISTING CONCEPTS (IDs): ${allIdeas.map(i => `${i.id}:${i.title}`).join(', ').substring(0, 1000)}`
    : "No concepts created yet.";

  if (activeIdea) {
    // CONCEPT MODE
    let structuralContext = "";

    if (activeIdea.sections && activeIdea.sections.length > 0) {
      structuralContext += `\n**STRUCTURED SECTIONS (HIERARCHY):**\n`;
      const rootSections = activeIdea.sections.filter(s => !s.parentId).sort((a, b) => (a.order || 0) - (b.order || 0));
      rootSections.forEach(root => {
        const cleanContent = root.content.replace(/<[^>]*>?/gm, '');
        structuralContext += `\n# [ID: ${root.id}] ${root.title}\n${cleanContent.substring(0, 1000)}\n`;
        const children = activeIdea.sections!.filter(s => s.parentId === root.id).sort((a, b) => (a.order || 0) - (b.order || 0));
        children.forEach(child => {
          const cleanChild = child.content.replace(/<[^>]*>?/gm, '');
          structuralContext += `\n## [ID: ${child.id}] ${child.title}\n${cleanChild.substring(0, 500)}\n`;
        });
      });
    } else {
      structuralContext = `CURRENT CONTENT:\n${(activeIdea.content || "").substring(0, 2000)}`;
    }

    return `${base}
    
    ### CONTEXT: ACTIVE PROJECT "${activeIdea.title}"
    ${structuralContext}

    ### ROLE: ACTIVE ARCHITECT
    You are building "${activeIdea.title}".
    *   Always offer an action to "Generate Sections" if the idea is empty.
    *   Always offer an action to "Refine Content" if sections exist.
    `;
  } else {
    // EPISODIC / GLOBAL MODE
    return `${base}
    
    ### CONTEXT: EPISODIC MODE (No active project)
    ${ideaListContext}

    ### ROLE: GLOBAL ARCHITECT
    *   Help the user crystallize a new idea.
    *   Action: "Create [Concept Name]"
    *   Action: "Switch to [Existing Concept]"
    `;
  }
};

// Default System Instruction (Defaults to Explorer for Live/Voice compatibility)
export const getSystemInstruction = getExplorerInstruction;

const generateId = () => Math.random().toString(36).substring(2, 9);

// --- INTENT DETECTION FOR VOICE DELEGATION ---
// Strong signals that indicate a need for real-time web search
// Requires interrogative + topical keyword combinations to avoid false positives

const INTERROGATIVES = ['what', 'what\'s', 'whats', 'how', 'how\'s', 'hows', 'is there', 'are there', 'where', 'when', 'tell me', 'show me', 'find', 'search', 'look up', 'check'];

const REAL_TIME_TOPICS = [
  // Weather (strong signal)
  'weather', 'forecast', 'temperature', 'raining', 'snowing', 'sunny', 'cloudy',
  // News (strong signal)
  'news', 'headlines', 'happening in', 'going on in',
  // Events/Local (strong signal)
  'events near', 'concerts', 'shows tonight', 'playing at', 'open right now',
  'restaurants near', 'places to eat', 'things to do',
  // Sports/Finance (strong signal)
  'score of', 'who won', 'stock price', 'trading at',
  // Time/Date (strong signal)
  'date', 'time', 'day is it', 'year is it', 'when is',
  // Explicit search
  'google', 'look up', 'search for', 'who is', 'what is'
];

// Phrases that are strong standalone signals (no interrogative needed)

export const detectSearchIntent = (text: string): boolean => {
  const lower = text.toLowerCase();

  // Explicit search triggers
  const explicitTriggers = [
    'search for', 'look up', 'google', 'find info', 'find information',
    'what is', 'who is', 'when was', 'where is', 'latest', 'current',
    'news', 'today', 'weather', 'happening now', 'recent'
  ];

  if (explicitTriggers.some(t => lower.includes(t))) return true;

  // Question patterns that imply external knowledge
  const questionPatterns = [
    /^(what|who|where|when|why) (is|are|was|were|do|does|did)/i,
    /how (many|much|old|tall|long|far)/i,
    /latest on/i,
    /news about/i,
    /update on/i
  ];

  return questionPatterns.some(p => p.test(text));
};

// Briefing search - used by voice mode to delegate to text search
export const performBriefingSearch = async (
  query: string,
  activeIdea: Idea | null,
  allIdeas: Idea[]
): Promise<{ text: string; sources: { uri: string; title: string }[] }> => {
  // Use the universal search service (backed by SearXNG)
  try {
    const { context, sources } = await performSearch(query);

    // If no results, return empty
    if (!sources || sources.length === 0) {
      return {
        text: 'No results found.',
        sources: []
      };
    }

    return {
      text: context,
      sources: sources.map(s => ({ uri: s.url, title: s.title }))
    };
  } catch (e) {
    console.error("Search failed:", e);
    return {
      text: 'Search unavailable.',
      sources: []
    };
  }
};

export const sendMessageToGemini = async (
  messages: Message[],
  userPrompt: string,
  activeIdea: Idea | null,
  allIdeas: Idea[],
  isArchitectMode: boolean = true,
  creativityLevel?: number
): Promise<{
  text: string;
  groundingSources?: { uri: string; title: string }[];
  proposal?: any;
  planUpdate?: any;
  suggestedActions?: SuggestedAction[];
  modelUsed?: string;
}> => {
  const ai = new GoogleGenAI({ apiKey: config.gemini.apiKey || process.env.API_KEY });

  // Get the latest user message for research detection
  const latestUserMessage = messages.filter(m => m.role === MessageRole.USER).pop();
  const queryText = latestUserMessage?.text || userPrompt;

  // TWO-PHASE RESEARCH FOR ARCHITECT MODE
  // If user needs research info, fetch it first and inject as context
  let researchContext = "";
  let researchSources: { uri: string; title: string }[] = [];

  if (isArchitectMode && detectSearchIntent(queryText)) {
    try {
      const research = await performBriefingSearch(queryText, activeIdea, allIdeas);
      if (research.text && research.text !== 'No results found.') {
        researchContext = `\n\n=== WEB RESEARCH RESULTS ===\n${research.text}\n=== END RESEARCH ===\n`;
        researchSources = research.sources;
      }
    } catch (e) {
      // If research fails, continue without it - non-blocking
      console.warn("Research phase failed, continuing without web context");
    }
  }

  const contents = messages
    .filter(m => m.role !== MessageRole.SYSTEM)
    .map(m => ({
      role: m.role === MessageRole.USER ? 'user' : 'model',
      parts: [{ text: m.text }]
    }));

  // If we have research context, append it to the system instruction
  let systemInstruction = isArchitectMode
    ? getArchitectInstruction(activeIdea, allIdeas)
    : getExplorerInstruction(activeIdea, allIdeas);

  if (researchContext) {
    systemInstruction += researchContext;
  }

  // Gemini API doesn't support combining googleSearch with functionDeclarations
  // Architect mode: Use function calling for structured proposals (research done in pre-phase)
  // Explorer mode: Use Google Search for real-time information
  const tools = isArchitectMode
    ? [{ functionDeclarations: toolsDef }]
    : [{ googleSearch: {} }];

  // For Explorer mode: implement fallback chain for overloaded servers
  // Lighter models are more likely to respond under heavy load
  let response: any;
  let modelUsed = config.gemini.textModel;

  if (!isArchitectMode) {
    // Explorer mode: try fallback chain on overload errors
    let currentModel = config.gemini.textModel;
    let lastError: any = null;

    while (true) {
      try {
        console.log(`[Explorer] Attempting request with model: ${currentModel}`);
        response = await ai.models.generateContent({
          model: currentModel,
          contents: contents,
          config: {
            systemInstruction: systemInstruction,
            tools: tools,
            temperature: creativityLevel !== undefined ? 0.1 + (creativityLevel * 0.8) : 0.4
          }
        });
        modelUsed = currentModel;
        if (currentModel !== config.gemini.textModel) {
          console.log(`[Explorer] Fallback successful: ${currentModel}`);
        }
        break; // Success, exit loop
      } catch (error: any) {
        lastError = error;

        if (isOverloadError(error)) {
          const nextModel = getNextFallbackModel(currentModel);
          if (nextModel) {
            console.warn(`[Explorer] Model ${currentModel} overloaded, falling back to ${nextModel}`);
            currentModel = nextModel;
            continue; // Try next model
          }
        }
        // Either not an overload error, or no more fallbacks
        throw lastError;
      }
    }
  } else {
    // Architect mode: no fallback (function calling requires consistent model)
    response = await ai.models.generateContent({
      model: config.gemini.textModel,
      contents: contents,
      config: {
        systemInstruction: systemInstruction,
        tools: tools,
        temperature: creativityLevel !== undefined ? 0.1 + (creativityLevel * 0.8) : 0.2
      }
    });
    modelUsed = config.gemini.textModel;
  }

  // Extract grounding sources
  const groundingSources = response.candidates?.[0]?.groundingMetadata?.groundingChunks
    ?.map((c: any) => c.web ? { uri: c.web.uri, title: c.web.title } : null)
    .filter((x: any) => x !== null);

  // Parse Text for Thinking and Actions
  let fullText = response.text || '';
  let suggestedActions: SuggestedAction[] | undefined = undefined;

  // Thinking Block is now prevalent in BOTH modes, so we always check for it.
  // The system prompts differ in usage:
  // Architect: "The Construct"
  // Explorer: "Deep Reasoning"

  // Extract <actions> block (Architect Mode primarily, but harmless to check)
  const actionsMatch = fullText.match(/<actions>([\s\S]*?)<\/actions>/);
  if (actionsMatch) {
    try {
      suggestedActions = JSON.parse(actionsMatch[1]);
    } catch (e) {
      console.error("Failed to parse suggested actions JSON", e);
    }
    fullText = fullText.replace(/<actions>[\s\S]*?<\/actions>/, '').trim();
  }

  // Extract proposal from function calls
  let proposal = undefined;
  let planUpdate = undefined;

  const fcs = response.functionCalls;

  if (fcs && fcs.length > 0) {
    for (const fc of fcs) {
      if (fc.name === 'createConcept') {
        proposal = {
          type: 'create_concept',
          title: fc.args['title'],
          status: 'active',
          changes: [{
            id: generateId(),
            action: 'create_section',
            title: 'Overview',
            content: fc.args['initialContent'],
            reasoning: fc.args['reasoning'],
            status: 'pending'
          }]
        };
      } else if (fc.name === 'proposeBatchChanges') {
        const rawChanges = fc.args['changes'] as any[];
        if (Array.isArray(rawChanges)) {
          proposal = {
            type: 'batch_update',
            status: 'active',
            changes: rawChanges.map(c => ({
              id: generateId(),
              action: c.action,
              sectionId: c.sectionId,
              parentId: c.parentId,
              title: c.title,
              content: c.content,
              reasoning: c.reasoning,
              status: 'pending'
            }))
          };
        }
      } else if (fc.name === 'managePlan') {
        planUpdate = {
          title: fc.args['title'],
          steps: fc.args['steps']
        };
      }
    }
  }

  // Combine grounding sources from response with any pre-fetched research sources
  const allSources = [...(groundingSources || []), ...researchSources];

  return {
    text: fullText,
    groundingSources: allSources.length > 0 ? allSources : undefined,
    proposal,
    planUpdate,
    suggestedActions,
    modelUsed: modelUsed !== config.gemini.textModel ? modelUsed : undefined
  };
};

export const sendMessageStreamToGemini = async function* (
  messages: Message[],
  userPrompt: string,
  activeIdea: Idea | null,
  allIdeas: Idea[],
  isArchitectMode: boolean = true,
  creativityLevel?: number
): AsyncGenerator<string, void, unknown> {
  const ai = new GoogleGenAI({ apiKey: config.gemini.apiKey || process.env.API_KEY });

  // Get the latest user message for research detection
  const latestUserMessage = messages.filter(m => m.role === MessageRole.USER).pop();
  const queryText = latestUserMessage?.text || userPrompt;

  // TWO-PHASE RESEARCH FOR ARCHITECT MODE
  let researchContext = "";

  if (isArchitectMode && detectSearchIntent(queryText)) {
    try {
      const research = await performBriefingSearch(queryText, activeIdea, allIdeas);
      if (research.text && research.text !== 'No results found.') {
        researchContext = `\n\n=== WEB RESEARCH RESULTS ===\n${research.text}\n=== END RESEARCH ===\n`;
      }
    } catch (e) {
      console.warn("Research phase failed, continuing without web context");
    }
  }

  const contents = messages
    .filter(m => m.role !== MessageRole.SYSTEM)
    .map(m => ({
      role: m.role === MessageRole.USER ? 'user' : 'model',
      parts: [{ text: m.text }]
    }));

  let systemInstruction = isArchitectMode
    ? getArchitectInstruction(activeIdea, allIdeas)
    : getExplorerInstruction(activeIdea, allIdeas);

  if (researchContext) {
    systemInstruction += researchContext;
  }

  // Gemini API tools setup
  const tools = isArchitectMode
    ? [{ functionDeclarations: toolsDef }]
    : [{ googleSearch: {} }];

  // Streaming request
  try {
    const result = await ai.models.generateContentStream({
      model: config.gemini.textModel,
      contents: contents,
      config: {
        systemInstruction: systemInstruction,
        tools: tools,
        temperature: creativityLevel !== undefined ? 0.1 + (creativityLevel * 0.8) : (isArchitectMode ? 0.2 : 0.4)
      }
    });

    for await (const chunk of result.stream) {
      const chunkText = chunk.text();
      if (chunkText) {
        yield chunkText;
      }
    }
  } catch (error: any) {
    console.error("Gemini Streaming Error:", error);
    throw error;
  }
};

// --- EXPORT & GENERATION SERVICES ---

export const generatePresentationData = async (idea: Idea) => {
  const ai = new GoogleGenAI({ apiKey: config.gemini.apiKey || process.env.API_KEY });

  const context = `
    Project: ${idea.title}
    Content: ${idea.sections?.map(s => `${s.title}: ${s.content.replace(/<[^>]*>/g, '')}`).join('\n\n')}
    `;

  const prompt = `
    Act as a professional presentation designer. 
    Analyze the project content provided below and construct a comprehensive PowerPoint presentation structure.
    
    **CRITICAL INSTRUCTION:** 
    Do NOT stick to a fixed number of slides. 
    Determine the optimal number of slides (ranging from 5 to 30+) based strictly on the complexity, depth, and volume of the content.
    - If the content is brief, create a concise pitch deck (5-8 slides).
    - If the content is extensive, create a detailed technical deck (20+ slides) that covers every section thoroughly.
    
    Ensure the narrative flow is logical:
    1. Title Slide & Executive Summary
    2. Problem Statement / Context
    3. Detailed Solutions/Concepts (Break down every major section into its own slide or series of slides)
    4. Implementation / Roadmap
    5. Conclusion / Next Steps
    
    Return JSON ONLY with this schema:
    {
        "slides": [
            {
                "title": "Slide Title",
                "bullets": ["Point 1", "Point 2", "Point 3"],
                "speakerNotes": "Detailed speaker notes explaining the content of this slide..."
            }
        ]
    }
    `;

  const response = await ai.models.generateContent({
    model: config.gemini.textModel,
    contents: [
      { role: 'user', parts: [{ text: context }, { text: prompt }] }
    ],
    config: {
      responseMimeType: "application/json",
      temperature: 0.2
    }
  });

  try {
    return JSON.parse(response.text || '{}');
  } catch (e) {
    console.error("Failed to parse presentation JSON", e);
    return { slides: [] };
  }
};

export const generateSpreadsheetData = async (idea: Idea) => {
  const ai = new GoogleGenAI({ apiKey: config.gemini.apiKey || process.env.API_KEY });

  const context = `
    Project: ${idea.title}
    Content: ${idea.sections?.map(s => `${s.title}: ${s.content.replace(/<[^>]*>/g, '')}`).join('\n\n')}
    `;

  const prompt = `
    Act as a Data Analyst.
    Extract key tabular data from this project to populate an Excel workbook.
    Identify things like: Bill of Materials (BOM), Cost Estimates, Timelines, Risk Logs, or Stakeholder Lists.
    Return JSON ONLY with this schema:
    {
        "sheets": [
            {
                "name": "Sheet Name (e.g., BOM)",
                "headers": ["Item", "Cost", "Qty"],
                "rows": [
                   ["Widget A", "$10", "5"],
                   ["Widget B", "$20", "2"]
                ]
            }
        ]
    }
    `;

  const response = await ai.models.generateContent({
    model: config.gemini.textModel,
    contents: [
      { role: 'user', parts: [{ text: context }, { text: prompt }] }
    ],
    config: {
      responseMimeType: "application/json",
      temperature: 0.2
    }
  });

  try {
    return JSON.parse(response.text || '{}');
  } catch (e) {
    console.error("Failed to parse spreadsheet JSON", e);
    return { sheets: [] };
  }
};

export const generateConceptImage = async (prompt: string, projectContext?: { title: string, sections?: { title: string, content: string }[] }) => {
  const ai = new GoogleGenAI({ apiKey: config.gemini.apiKey || process.env.API_KEY });

  let enhancedPrompt = prompt;

  if (projectContext) {
    const sectionKeywords = projectContext.sections
      ?.map(s => s.title)
      .slice(0, 5)
      .join(', ') || '';

    const contextHint = `
Project: "${projectContext.title}"
Key themes: ${sectionKeywords}

User's image request: ${prompt}

Generate a professional, high-quality visual that relates to this project context while fulfilling the user's specific request. Style: Modern, clean, suitable for professional presentations and documents.`;

    enhancedPrompt = contextHint;
  }

  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash-image',
    contents: { parts: [{ text: enhancedPrompt }] },
    config: {}
  });

  for (const part of response.candidates?.[0]?.content?.parts || []) {
    if (part.inlineData) {
      return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
    }
  }
  return null;
};